import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Row,
  Col,
  Statistic,
  Typography,
  Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  StarOutlined,
  StarFilled,
  DollarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { currencyService, Currency } from '@/services/currency.service';

const { Title, Text } = Typography;
const { Search } = Input;

export const CurrenciesPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const isBaseCurrencyWatch = Form.useWatch('is_base_currency', form);

  useEffect(() => {
    loadCurrencies();
  }, []);

  useEffect(() => {
    filterCurrencies();
  }, [searchText, currencies]);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      const data = await currencyService.getCurrencies();
      setCurrencies(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('errLoadCurrencies'));
    } finally {
      setLoading(false);
    }
  };

  const filterCurrencies = () => {
    if (!searchText) {
      setFilteredCurrencies(currencies);
      return;
    }

    const filtered = currencies.filter(
      (currency) =>
        currency.currency_code.toLowerCase().includes(searchText.toLowerCase()) ||
        currency.currency_name.toLowerCase().includes(searchText.toLowerCase()) ||
        currency.symbol?.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredCurrencies(filtered);
  };

  const handleAdd = () => {
    setEditingCurrency(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      exchange_rate: 1,
      is_base_currency: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    form.setFieldsValue({
      currency_code: currency.currency_code,
      currency_name: currency.currency_name,
      symbol: currency.symbol,
      exchange_rate: Number(currency.exchange_rate),
      is_active: currency.is_active,
      is_base_currency: currency.is_base_currency,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await currencyService.deleteCurrency(id);
      message.success(t('currencyDeleted'));
      loadCurrencies();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('errDeleteCurrency'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetBaseCurrency = async (currency: Currency) => {
    try {
      setLoading(true);
      await currencyService.setBaseCurrency(currency.id);
      message.success(t('currencySetAsBase', { code: currency.currency_code }));
      loadCurrencies();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('errSetBaseCurrency'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingCurrency?.is_base_currency || form.getFieldValue('is_base_currency')) {
        form.setFieldValue('exchange_rate', 1);
      }
      const values = await form.validateFields();
      setLoading(true);

      // Ensure currency_code is uppercase
      values.currency_code = values.currency_code.toUpperCase();
      values.exchange_rate = Number(values.exchange_rate);

      if (editingCurrency) {
        await currencyService.updateCurrency(editingCurrency.id, values);
        message.success(t('currencyUpdated'));
      } else {
        await currencyService.createCurrency(values);
        message.success(t('currencyCreated'));
      }

      setModalVisible(false);
      form.resetFields();
      loadCurrencies();
    } catch (error: any) {
      if (error.errorFields) {
        // Validation error
        return;
      }
      message.error(error.response?.data?.message || t('errSaveCurrency'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Currency> = [
    {
      title: t('currencyCode'),
      dataIndex: 'currency_code',
      key: 'currency_code',
      width: 150,
      render: (code: string, record: Currency) => (
        <Space>
          <strong>{code}</strong>
          {record.is_base_currency && (
            <Tag color="gold" icon={<StarFilled />}>
              {t('baseTag')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('currencyName'),
      dataIndex: 'currency_name',
      key: 'currency_name',
    },
    {
      title: t('symbol'),
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100,
      align: 'center',
      render: (symbol: string) => symbol || '-',
    },
    {
      title: t('exchangeRate'),
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 150,
      align: 'right',
      render: (rate: number, record: Currency) => (
        <span>
          {record.is_base_currency ? (
            <Tag color="blue">1.000000</Tag>
          ) : (
            <Text>{Number(rate).toFixed(6)}</Text>
          )}
        </span>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? t('active') : t('inactive')}</Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record: Currency) => (
        <Space>
          {!record.is_base_currency && (
            <Popconfirm
              title={t('setAsBaseCurrency')}
              description={t('setAsBaseConfirm')}
              onConfirm={() => handleSetBaseCurrency(record)}
              okText={tc('yes')}
              cancelText={tc('no')}
            >
              <Button type="link" icon={<StarOutlined />} size="small">
                {t('setAsBase')}
              </Button>
            </Popconfirm>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {tc('edit')}
          </Button>
          {!record.is_base_currency && (
            <Popconfirm
              title={t('deleteCurrency')}
              description={t('confirmDeleteCurrency')}
              onConfirm={() => handleDelete(record.id)}
              okText={tc('yes')}
              cancelText={tc('no')}
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                {tc('delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const baseCurrency = currencies.find((c) => c.is_base_currency);
  const activeCurrencies = currencies.filter((c) => c.is_active).length;

  return (
    <div>
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('totalCurrencies')}
              value={currencies.length}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('activeCurrencies')}
              value={activeCurrencies}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('baseCurrency')}
              value={baseCurrency?.currency_code || t('baseCurrencyNotSet')}
              prefix={<StarFilled />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table */}
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {t('currencies')}
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder={t('searchCurrencies')}
              allowClear
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            <Button icon={<ReloadOutlined />} onClick={loadCurrencies}>
              {tc('refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {t('addCurrency')}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredCurrencies}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('totalCurrenciesCount', { count: total }),
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingCurrency ? t('editCurrency') : t('addNewCurrency')}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={tc('save')}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('currencyCode')}
                name="currency_code"
                rules={[
                  { required: true, message: t('enterCurrencyCode') },
                  {
                    pattern: /^[A-Z]{3}$/,
                    message: t('currencyCodePattern'),
                  },
                ]}
              >
                <Input
                  placeholder="USD"
                  maxLength={3}
                  disabled={!!editingCurrency}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('symbol')}
                name="symbol"
                rules={[{ max: 10, message: t('symbolMaxLength') }]}
              >
                <Input placeholder="$" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t('currencyName')}
            name="currency_name"
            rules={[
              { required: true, message: t('enterCurrencyName') },
              { min: 2, message: t('currencyNameMin') },
              { max: 100, message: t('currencyNameMax') },
            ]}
          >
            <Input placeholder="US Dollar" />
          </Form.Item>

          <Form.Item
            label={t('exchangeRateRelative')}
            name="exchange_rate"
            rules={[
              { required: true, message: t('enterExchangeRate') },
              {
                type: 'number',
                min: 0.000001,
                message: t('exchangeRateMustBePositive'),
              },
            ]}
            tooltip={t('exchangeRateTooltip')}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="1.0"
              min={0.000001}
              step={0.01}
              precision={6}
              readOnly={!!editingCurrency?.is_base_currency || !!isBaseCurrencyWatch}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('setAsBaseCurrency')}
                name="is_base_currency"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren={tc('yes')}
                  unCheckedChildren={tc('no')}
                  onChange={(checked) => {
                    if (checked) {
                      form.setFieldsValue({ exchange_rate: 1 });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('active')} name="is_active" valuePropName="checked">
                <Switch checkedChildren={tc('yes')} unCheckedChildren={tc('no')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CurrenciesPage;
